import { useAIAgents } from '@/hooks/useAIAgents';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Brain, Activity, TrendingUp, DollarSign, Clock, Zap } from 'lucide-react';

export const AgentAnalytics = () => {
  const { agents, useAgentAnalytics, useAgentExecutions } = useAIAgents();
  const { data: analytics } = useAgentAnalytics();
  const { data: executions = [] } = useAgentExecutions();

  // Prepare data for charts
  const agentTypeData = agents.reduce((acc, agent) => {
    const type = agent.agent_type;
    if (!acc[type]) {
      acc[type] = { name: type.replace(/_/g, ' '), count: 0, executions: 0 };
    }
    acc[type].count += 1;
    acc[type].executions += agent.execution_count;
    return acc;
  }, {} as Record<string, any>);

  const chartData = Object.values(agentTypeData);

  const executionTrend = executions
    .slice(0, 30)
    .reduce((acc, execution) => {
      const date = new Date(execution.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, completed: 0, failed: 0, total: 0 };
      }
      acc[date].total += 1;
      if (execution.status === 'completed') {
        acc[date].completed += 1;
      } else if (execution.status === 'failed') {
        acc[date].failed += 1;
      }
      return acc;
    }, {} as Record<string, any>);

  const trendData = Object.values(executionTrend).slice(-7);

  const statusData = [
    { name: 'Active', value: agents.filter(a => a.status === 'active').length, color: '#10b981' },
    { name: 'Inactive', value: agents.filter(a => a.status === 'inactive').length, color: '#6b7280' },
    { name: 'Paused', value: agents.filter(a => a.status === 'paused').length, color: '#f59e0b' },
    { name: 'Error', value: agents.filter(a => a.status === 'error').length, color: '#ef4444' },
  ].filter(item => item.value > 0);

  const topPerformers = agents
    .filter(agent => agent.execution_count > 0)
    .sort((a, b) => b.success_rate - a.success_rate)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Performance Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.avg_success_rate ? `${Math.round(analytics.avg_success_rate)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all active agents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${analytics?.total_cost ? (analytics.total_cost || 0).toFixed(2) : '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.total_tokens_used?.toLocaleString() || '0'} tokens used
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Execution Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agents.length > 0 
                ? `${Math.round(agents.reduce((sum, a) => sum + (a.average_execution_time_ms || 0), 0) / agents.length)}ms`
                : '0ms'
              }
            </div>
            <p className="text-xs text-muted-foreground">
              Average response time
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Agent Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Agents by Type</CardTitle>
            <CardDescription>Distribution of agent types and their execution counts</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Bar dataKey="count" fill="hsl(var(--primary))" name="Count" />
                <Bar dataKey="executions" fill="hsl(var(--secondary))" name="Executions" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Agent Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Agent Status</CardTitle>
            <CardDescription>Current status distribution of all agents</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Execution Trend */}
      {trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Execution Trend (Last 7 Days)</CardTitle>
            <CardDescription>Daily execution success and failure rates</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Line 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Completed"
                />
                <Line 
                  type="monotone" 
                  dataKey="failed" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="Failed"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Agents</CardTitle>
            <CardDescription>Agents with the highest success rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPerformers.map((agent, index) => (
                <div key={agent.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="text-lg font-semibold text-muted-foreground">
                      #{index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{agent.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {agent.agent_type.replace(/_/g, ' ')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary" className="mb-1">
                      {Math.round(agent.success_rate)}% success
                    </Badge>
                    <div className="text-sm text-muted-foreground">
                      {agent.execution_count} executions
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {agents.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Brain className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">No analytics yet</h3>
            <p className="text-muted-foreground">
              Create and run some AI agents to see analytics data.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};